/**
 * Custom hook providing message operations for the knowledge graph chat.
 * Operates on Apollo reactive variables for state management.
 * @remarks
 * Provides immutable operations for managing chat messages. All operations
 * create new arrays/objects rather than mutating existing state, following
 * functional programming principles established in the codebase.
 *
 * Operations include:
 * - Adding messages to the chat history
 * - Updating existing messages by ID
 * - Removing messages by ID
 * - Clearing all messages
 *
 * All operations are wrapped in useCallback for stable references across renders.
 * @returns Message operation functions for chat state management
 */

import { useCallback } from "react";
import type { IMessage } from "react-native-gifted-chat";

import { messagesVar } from "@/stores/knowledgeGraph";

/**
 * Hook providing message operations for knowledge graph chat.
 * @returns Object containing message operation functions
 */
export const useMessageOperations = () => {
  /**
   * Add a new message to the chat.
   * @param message - Message to add
   */
  const addMessage = useCallback((message: IMessage) => {
    const currentMessages = messagesVar();
    messagesVar([...currentMessages, message]);
  }, []);

  /**
   * Clear all messages from the chat.
   */
  const clearMessages = useCallback(() => {
    messagesVar([]);
  }, []);

  /**
   * Update an existing message by ID.
   * @param messageId - ID of message to update
   * @param updates - Partial message object with properties to update
   */
  const updateMessage = useCallback(
    (messageId: string, updates: Partial<IMessage>) => {
      const currentMessages = messagesVar();
      const updatedMessages = currentMessages.map(msg =>
        msg._id === messageId ? { ...msg, ...updates } : msg
      );
      messagesVar(updatedMessages);
    },
    []
  );

  /**
   * Remove a message from the chat by ID.
   * @param messageId - ID of message to remove
   */
  const removeMessage = useCallback((messageId: string) => {
    const currentMessages = messagesVar();
    const filteredMessages = currentMessages.filter(
      msg => msg._id !== messageId
    );
    messagesVar(filteredMessages);
  }, []);

  return {
    addMessage,
    clearMessages,
    updateMessage,
    removeMessage,
  };
};
